import React, { useCallback, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { 
  tokenomicsConfig, 
  generateVestingTimeline 
} from '@/data/tokenomicsData';
import { 
  Download, FileSpreadsheet, FileJson, 
  Terminal, Copy, Check, ExternalLink 
} from 'lucide-react';

export const DataAPISection = () => {
  const [copiedEndpoint, setCopiedEndpoint] = useState(null);
  
  // Export functions
  const exportCSV = useCallback(() => {
    const vestingData = generateVestingTimeline();
    const headers = ['Month', 'Circulating Supply %', 'Investor Unlock %', 'Team Unlock %'];
    const rows = vestingData.map(d => 
      [d.month, d.circulating.toFixed(2), d.investors.toFixed(2), d.team.toFixed(2)]
    );
    
    const csvContent = [headers, ...rows]
      .map(row => row.join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'neuro_vesting_schedule.csv';
    a.click();
    URL.revokeObjectURL(url);
  }, []);
  
  const exportXLSX = useCallback(() => {
    // For mock purposes, just download CSV with xlsx extension
    const vestingData = generateVestingTimeline();
    const headers = ['Month', 'Circulating Supply %', 'Investor Unlock %', 'Team Unlock %'];
    const rows = vestingData.map(d => 
      [d.month, d.circulating.toFixed(2), d.investors.toFixed(2), d.team.toFixed(2)]
    );
    
    const csvContent = [headers, ...rows]
      .map(row => row.join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'neuro_vesting_schedule.xlsx';
    a.click();
    URL.revokeObjectURL(url);
  }, []);
  
  const exportJSON = useCallback(() => {
    const data = {
      token: tokenomicsConfig.tokenName,
      totalSupply: tokenomicsConfig.totalSupply,
      initialFloat: tokenomicsConfig.initialFloat,
      allocations: tokenomicsConfig.allocations.map(a => ({
        id: a.id,
        name: a.name,
        percentage: a.percentage,
        unlockedAtTGE: a.unlockedAtTGE,
        vesting: a.vesting || null
      })),
      vestingTimeline: generateVestingTimeline()
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'neuro_tokenomics.json';
    a.click();
    URL.revokeObjectURL(url);
  }, []);
  
  const copyToClipboard = (text, endpoint) => {
    navigator.clipboard.writeText(text);
    setCopiedEndpoint(endpoint);
    setTimeout(() => setCopiedEndpoint(null), 2000);
  };
  
  const apiEndpoints = [
    { 
      method: 'GET', 
      path: '/api/tokenomics', 
      description: 'Get full tokenomics data',
      response: '{ allocations, metrics, ... }'
    },
    { 
      method: 'GET', 
      path: '/api/vesting', 
      description: 'Get vesting schedule',
      response: '{ timeline, investor, team }'
    },
    { 
      method: 'GET', 
      path: '/api/float', 
      description: 'Get current float data',
      response: '{ circulating, locked, ... }'
    },
  ];
  
  return (
    <section id="data" className="py-24 relative">
      {/* Background */}
      <div className="absolute inset-0 neural-grid opacity-20" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-primary text-sm font-medium tracking-wider uppercase">
            Data & API
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mt-3 mb-4">
            Download Data & API Access
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Export tokenomics data in your preferred format or access our API endpoints 
            for programmatic integration.
          </p>
        </motion.div>
        
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Download Section */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="glass rounded-2xl p-6 md:p-8"
          >
            <h3 className="font-heading text-xl font-semibold text-foreground mb-6 flex items-center gap-2">
              <Download className="w-5 h-5 text-primary" />
              Download Data
            </h3>
            
            <div className="space-y-4">
              <button
                onClick={exportCSV}
                className="w-full flex items-center gap-4 p-4 rounded-xl border border-border hover:border-primary/30 hover:bg-primary/5 transition-all group"
              >
                <div className="w-12 h-12 rounded-lg bg-success/10 flex items-center justify-center group-hover:bg-success/20 transition-colors">
                  <FileSpreadsheet className="w-6 h-6 text-success" />
                </div>
                <div className="text-left flex-1">
                  <h4 className="font-medium text-foreground">Tokenomics CSV</h4>
                  <p className="text-xs text-muted-foreground">Vesting schedule data</p>
                </div>
                <Download className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
              </button>
              
              <button
                onClick={exportXLSX}
                className="w-full flex items-center gap-4 p-4 rounded-xl border border-border hover:border-primary/30 hover:bg-primary/5 transition-all group"
              >
                <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                  <FileSpreadsheet className="w-6 h-6 text-accent" />
                </div>
                <div className="text-left flex-1">
                  <h4 className="font-medium text-foreground">Vesting Schedule XLSX</h4>
                  <p className="text-xs text-muted-foreground">Excel format</p>
                </div>
                <Download className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
              </button>
              
              <button
                onClick={exportJSON}
                className="w-full flex items-center gap-4 p-4 rounded-xl border border-border hover:border-primary/30 hover:bg-primary/5 transition-all group"
              >
                <div className="w-12 h-12 rounded-lg bg-warning/10 flex items-center justify-center group-hover:bg-warning/20 transition-colors">
                  <FileJson className="w-6 h-6 text-warning" />
                </div>
                <div className="text-left flex-1">
                  <h4 className="font-medium text-foreground">Full Data JSON</h4>
                  <p className="text-xs text-muted-foreground">Complete tokenomics structure</p>
                </div>
                <Download className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
              </button>
            </div>
          </motion.div>
          
          {/* API Section */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="glass rounded-2xl p-6 md:p-8"
          >
            <h3 className="font-heading text-xl font-semibold text-foreground mb-6 flex items-center gap-2">
              <Terminal className="w-5 h-5 text-primary" />
              API Endpoints
            </h3>
            
            {/* Terminal-style API display */}
            <div className="bg-background rounded-xl border border-border overflow-hidden">
              {/* Terminal header */}
              <div className="flex items-center gap-2 px-4 py-2 border-b border-border bg-muted/30">
                <div className="w-3 h-3 rounded-full bg-destructive/50" />
                <div className="w-3 h-3 rounded-full bg-warning/50" />
                <div className="w-3 h-3 rounded-full bg-success/50" />
                <span className="text-xs text-muted-foreground ml-2 font-mono">
                  api.neuro-mesh.io
                </span>
              </div>
              
              {/* API endpoints */}
              <div className="p-4 space-y-4 font-mono text-sm">
                {apiEndpoints.map((endpoint) => (
                  <div key={endpoint.path} className="group">
                    <div className="flex items-start gap-3">
                      <span className="px-2 py-0.5 rounded text-xs bg-success/20 text-success font-semibold">
                        {endpoint.method}
                      </span>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <code className="text-primary">{endpoint.path}</code>
                          <button
                            onClick={() => copyToClipboard(`https://api.neuro-mesh.io${endpoint.path}`, endpoint.path)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-muted rounded"
                          >
                            {copiedEndpoint === endpoint.path ? (
                              <Check className="w-4 h-4 text-success" />
                            ) : (
                              <Copy className="w-4 h-4 text-muted-foreground" />
                            )}
                          </button>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {endpoint.description}
                        </p>
                        <code className="text-xs text-muted-foreground/60 mt-1 block">
                          → {endpoint.response}
                        </code>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <p className="text-xs text-muted-foreground mt-4 text-center">
              API endpoints are mocked for demonstration purposes
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default DataAPISection;
